import type { FormEvent, ReactNode } from "react";
import { Modal, PortalButton } from "./PortalPrimitives";

/**
 * Diálogo de un formulario que ES el diálogo, sobre las TRES REGIONES del
 * `Modal` (revisión en vivo 2026-09-22, puntos #3 y #4A). Lo usan los
 * editores de contenido (Recompensas, Beneficios, Ofertas, Plantillas de
 * mensaje) y los formularios cuyo encabezado no es el título chico de
 * `FormLayout` sino un `h3.display` propio (Registrar puntos, Anular
 * movimiento, Revertir consumo, Añadir horario). Para un formulario con el
 * encabezado y las acciones estándar, el camino es `FormLayout` con su prop
 * `modal` — este componente es para cuando el encabezado es propio.
 *
 * Antes cada uno de esos formularios se renderizaba como `children` de un
 * `Modal` en su forma histórica, y dibujaba a mano su propio encabezado y su
 * propio pie con `padding` y divisores. Eso daba tres problemas a la vez:
 * el encabezado y el pie se desplazaban con el contenido en vez de quedar
 * fijos; el `padding: 24` del panel se sumaba al `24` del editor (48px); y
 * los divisores no llegaban a los bordes del panel. No se podía arreglar en
 * `EditorHeader`/`EditorFooter` sin cambiar a la vez a sus cuatro llamadores,
 * porque son ellos los que ponen el `Modal`.
 *
 * `display: contents` en el `<form>`: el formulario no debe tener caja propia
 * —el diálogo se posiciona contra el viewport, no contra él— pero sí tiene
 * que seguir siendo ANCESTRO del botón `type="submit"` que vive en el pie,
 * para que `onSubmit` se dispare sin `form=<id>` ni `requestSubmit`. Es el
 * mismo recurso que ya usa `FormLayout` en su forma `modal`.
 */
export interface EditorModalTarget {
  label: string;
  width?: number | string;
  onClose?: () => void;
  role?: "dialog" | "alertdialog";
}

export function EditorModal({
  modal,
  onSubmit,
  header,
  footer,
  children,
}: {
  /**
   * El destino se recibe como UN objeto, no como props sueltas: los seis
   * llamadores lo reciben así de su pantalla y desarmarlo en cada uno
   * (`label={modal.label}` + dos spreads condicionales para `width` y
   * `onClose`, que `exactOptionalPropertyTypes` obliga a escribir) repetía el
   * mismo bloque de tres líneas seis veces — jscpd lo marcaba como clon.
   */
  modal: EditorModalTarget;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
}) {
  return (
    <form onSubmit={onSubmit} noValidate style={{ display: "contents" }}>
      <Modal
        label={modal.label}
        {...(modal.width === undefined ? {} : { width: modal.width })}
        {...(modal.onClose ? { onClose: modal.onClose } : {})}
        {...(modal.role ? { role: modal.role } : {})}
        header={header}
        footer={footer}
      >
        {children}
      </Modal>
    </form>
  );
}

/**
 * Fila de acciones del pie de un diálogo: "Cancelar" a la izquierda y la
 * acción de confirmación a la derecha, alineadas al borde derecho.
 *
 * Existe por la misma razón que `EditorModal`: el mismo bloque —contenedor
 * `flex` + `PortalButton` secundario "Cancelar"— quedaba repetido en cada
 * diálogo migrado (Registrar puntos, Anular movimiento, Revertir consumo,
 * Añadir horario) y jscpd lo marcaba como clon. `children` es la acción de
 * confirmación, que sí cambia en cada uno (rótulo y tono).
 *
 * Los editores de contenido NO lo usan: su pie es `EditorFooter`, que además
 * condiciona la acción por permiso con `Can`.
 */
export function DialogActions({
  onCancel,
  cancelLabel = "Cancelar",
  children,
}: {
  onCancel: () => void;
  cancelLabel?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
      <PortalButton
        type="button"
        tone="secondary"
        size="small"
        onClick={onCancel}
      >
        {cancelLabel}
      </PortalButton>
      {children}
    </div>
  );
}
